import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { recipes } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/MyRecipes.css';

const MyRecipes = () => {
  const [myRecipes, setMyRecipes] = useState([]);
  const [draftRecipes, setDraftRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('published'); // 'published' or 'drafts'
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    // Check for success message from navigation state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Set active tab based on navigation state
      if (location.state.tab === 'drafts') {
        setActiveTab('drafts');
      }
      // Clear the message from location state
      navigate(location.pathname, { replace: true, state: {} });

      // Auto-hide message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }

    fetchRecipes();
  }, [user, authLoading]);

  const fetchRecipes = async () => {
    try {
      const [publishedResponse, draftsResponse] = await Promise.all([
        recipes.getMyRecipes(),
        recipes.getDrafts()
      ]);
      setMyRecipes(publishedResponse.data);
      setDraftRecipes(draftsResponse.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, isDraft = false) => {
    if (window.confirm('Biztosan törölni szeretnéd ezt a receptet?')) {
      try {
        await recipes.delete(id);
        if (isDraft) {
          setDraftRecipes(draftRecipes.filter(recipe => recipe._id !== id));
        } else {
          setMyRecipes(myRecipes.filter(recipe => recipe._id !== id));
        }
      } catch (error) {
        console.error('Error deleting recipe:', error);
        alert('Hiba történt a recept törlése során');
      }
    }
  };

  const handlePublishDraft = async (id) => {
    if (window.confirm('Biztosan közzéteszed ezt a piszkozatot? Ez után már nem lesz piszkozat.')) {
      try {
        const response = await recipes.publishDraft(id);
        // Remove from drafts
        setDraftRecipes(draftRecipes.filter(recipe => recipe._id !== id));
        // Refresh published recipes
        const publishedResponse = await recipes.getMyRecipes();
        setMyRecipes(publishedResponse.data);
        // Show success message
        setSuccessMessage(response.data.message || 'Piszkozat sikeresen közzétéve!');
        setTimeout(() => setSuccessMessage(''), 5000);
      } catch (error) {
        console.error('Error publishing draft:', error);
        alert(error.response?.data?.message || 'Hiba történt a piszkozat közzétételekor');
      }
    }
  };

  if (loading) {
    return <div className="container loading">Betöltés...</div>;
  }

  const currentRecipes = activeTab === 'published' ? myRecipes : draftRecipes;

  return (
    <div className="my-recipes-page">
      <div className="container">
        <div className="page-header">
          <h1>Saját Receptjeim</h1>
          <Link to="/add-recipe" className="btn btn-primary">
            + Új Recept
          </Link>
        </div>

        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: '2rem' }}>
            {successMessage}
          </div>
        )}

        {/* Tabs */}
        <div className="tabs" style={{ marginBottom: '2rem' }}>
          <button
            className={`tab ${activeTab === 'published' ? 'active' : ''}`}
            onClick={() => setActiveTab('published')}
          >
            Közzétett receptek ({myRecipes.length})
          </button>
          <button
            className={`tab ${activeTab === 'drafts' ? 'active' : ''}`}
            onClick={() => setActiveTab('drafts')}
          >
            📝 Piszkozatok ({draftRecipes.length})
          </button>
        </div>

        {currentRecipes.length === 0 ? (
          <div className="no-recipes">
            <p>
              {activeTab === 'published'
                ? 'Még nem hoztál létre receptet.'
                : 'Nincs mentett piszkozatod.'}
            </p>
            <Link to="/add-recipe" className="btn btn-primary">
              {activeTab === 'published' ? 'Első recept létrehozása' : 'Új recept létrehozása'}
            </Link>
          </div>
        ) : (
          <div className="recipes-table">
            <table>
              <thead>
                <tr>
                  <th>Cím</th>
                  <th>Kategória</th>
                  <th>Nehézség</th>
                  <th>Státusz</th>
                  {activeTab === 'published' && <th>Kedvelések</th>}
                  <th>Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {currentRecipes.map((recipe) => (
                  <tr key={recipe._id}>
                    <td>
                      {activeTab === 'published' ? (
                        <Link to={`/recipe/${recipe._id}`} className="recipe-title-link">
                          {recipe.title || 'Cím nélküli recept'}
                        </Link>
                      ) : (
                        <span className="recipe-title-draft">
                          {recipe.title || 'Cím nélküli piszkozat'}
                        </span>
                      )}
                    </td>
                    <td>{recipe.category}</td>
                    <td>{recipe.difficulty}</td>
                    <td>
                      {activeTab === 'published' ? (
                        <span className={`status-badge ${recipe.isApproved ? 'approved' : 'pending'}`}>
                          {recipe.isApproved ? 'Jóváhagyva' : 'Várakozik'}
                        </span>
                      ) : (
                        <span className="status-badge draft">Piszkozat</span>
                      )}
                    </td>
                    {activeTab === 'published' && <td>{recipe.likes?.length || 0}</td>}
                    <td className="actions">
                      {activeTab === 'published' ? (
                        <>
                          <Link to={`/recipe/${recipe._id}`} className="btn btn-sm btn-secondary">
                            Megtekintés
                          </Link>
                          <Link to={`/edit-recipe/${recipe._id}`} className="btn btn-sm btn-primary">
                            Szerkesztés
                          </Link>
                          <button
                            onClick={() => handleDelete(recipe._id, false)}
                            className="btn btn-sm btn-outline"
                          >
                            Törlés
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to={`/edit-recipe/${recipe._id}`} className="btn btn-sm btn-primary">
                            Folytatás
                          </Link>
                          <button
                            onClick={() => handlePublishDraft(recipe._id)}
                            className="btn btn-sm btn-secondary"
                            title="Piszkozat közzététele"
                          >
                            Közzététel
                          </button>
                          <button
                            onClick={() => handleDelete(recipe._id, true)}
                            className="btn btn-sm btn-outline"
                          >
                            Törlés
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRecipes;
